import React, { useState, useEffect } from "react";
import BookService from "services/books.service";
import { CopyToClipboard } from 'react-copy-to-clipboard';

// Component for individual Tweet Card
const TweetCard = ({ book }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000); // Reset after 2s
    };

    return (
        <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 p-4 flex flex-col md:flex-row gap-4">
            {/* Book Image */}
            <div className="w-full md:w-24 flex-shrink-0">
                <img
                    src={book.image}
                    alt={book.title}
                    className="w-full h-auto rounded-md object-cover shadow-sm"
                    style={{ aspectRatio: '1/1.5' }} // Enforce aspect ratio
                />
            </div>

            {/* Content */}
            <div className="flex-grow flex flex-col justify-between">
                <div>
                    <h3 className="font-bold text-lg text-gray-800 leading-tight mb-1">{book.title}</h3>
                    <p className="text-sm text-gray-600 mb-3">{book.author}</p>

                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 relative group">
                        <p className="text-gray-700 italic text-sm">"{book.tweet}"</p>
                        <div className="absolute top-2 right-2 text-xs text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity">
                            {book.tweet.length} chars
                        </div>
                    </div>
                </div>

                <div className="mt-3 flex justify-end">
                    <CopyToClipboard text={book.tweet} onCopy={handleCopy}>
                        <button
                            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center gap-2 ${copied ? 'bg-green-100 text-green-700' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
                        >
                            {copied ? (
                                <>
                                    <i className="fas fa-check"></i> Copied!
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-copy"></i> Copy Tweet
                                </>
                            )}
                        </button>
                    </CopyToClipboard>
                </div>
            </div>
        </div>
    );
};

export default function TweetDashboard() {
    const [books, setBooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        fetchTweets();
    }, [page]);

    const fetchTweets = async () => {
        try {
            setLoading(true);
            const response = await BookService.getBookTweets(page);

            setBooks(response.data.books);
            setTotalPages(response.data.totalPages);
        } catch (error) {
            console.error("Error fetching tweets:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 p-6 md:p-12">
            <div className="max-w-4xl mx-auto">
                <header className="mb-8 flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Tweet Dashboard</h1>
                        <p className="text-gray-500 mt-1">Manage and copy generated tweets for new books.</p>
                    </div>
                    {/* Minimal Pagination Controls */}
                    <div className="flex gap-2">
                        <button
                            disabled={page === 1}
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            className="p-2 rounded-full bg-white shadow hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <i className="fas fa-chevron-left"></i>
                        </button>
                        <span className="self-center text-sm font-medium text-gray-600">
                            {page} / {totalPages}
                        </span>
                        <button
                            disabled={page === totalPages}
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            className="p-2 rounded-full bg-white shadow hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <i className="fas fa-chevron-right"></i>
                        </button>
                    </div>
                </header>

                {loading ? (
                    <div className="flex justify-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                ) : books.length > 0 ? (
                    <div className="grid gap-6">
                        {books.map(book => (
                            <TweetCard key={book._id} book={book} />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-20 bg-white rounded-xl shadow-sm">
                        <p className="text-gray-500 text-lg">No tweets generated yet.</p>
                    </div>
                )}
            </div>
        </div>
    );
}
